import { Languages, Monitor, Moon, Sun, type LucideIcon } from 'lucide-react'
import { RadioGroup } from 'radix-ui'
import { useTranslation } from 'react-i18next'
import { LANGUAGES, type Language } from '@/i18n'
import { cn } from '@/utils/cn'
import { usePreferences, type Theme } from '../store'

const themeOptions: { value: Theme; label: string; icon: LucideIcon }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
]

const segment =
  'flex items-center justify-center gap-1.5 rounded-[0.55rem] px-3 py-2 text-xs font-medium text-muted transition-all hover:text-ink data-[state=checked]:bg-surface data-[state=checked]:text-brand-700 data-[state=checked]:shadow-card'

/** Segmented Light / Dark / System control. */
export function ThemePicker({ className }: { className?: string }) {
  const { t } = useTranslation('account')
  const { theme, setTheme } = usePreferences()
  return (
    <RadioGroup.Root
      value={theme}
      onValueChange={(v) => setTheme(v as Theme)}
      aria-label={t('Theme')}
      className={cn('grid grid-cols-3 gap-1 rounded-xl border border-line bg-surface-sunken p-1', className)}
    >
      {themeOptions.map(({ value, label, icon: Icon }) => (
        <RadioGroup.Item key={value} value={value} className={segment}>
          <Icon className="size-4" aria-hidden="true" />
          {t(label)}
        </RadioGroup.Item>
      ))}
    </RadioGroup.Root>
  )
}

/** Segmented Français / English control. Language names are shown in their own language. */
export function LanguagePicker({ className, compact = false }: { className?: string; compact?: boolean }) {
  const { t } = useTranslation('account')
  const { locale, setLocale } = usePreferences()
  return (
    <RadioGroup.Root
      value={locale}
      onValueChange={(v) => setLocale(v as Language)}
      aria-label={t('Language')}
      className={cn('grid grid-cols-2 gap-1 rounded-xl border border-line bg-surface-sunken p-1', className)}
    >
      {LANGUAGES.map((l) => (
        <RadioGroup.Item key={l.code} value={l.code} lang={l.code} className={segment}>
          {compact ? null : <Languages className="size-4" aria-hidden="true" />}
          {compact ? l.short : l.label}
        </RadioGroup.Item>
      ))}
    </RadioGroup.Root>
  )
}
